import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  Search,
  RefreshCw,
  Download,
  TrendingUp,
  IndianRupee,
} from "lucide-react";

export const CityAdminCustomersReport = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("total_rides");

  useEffect(() => {
    if (user?.id) fetchData();
  }, [user?.id, sortBy]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("sort_by", sortBy);
      if (searchTerm) params.append("search", searchTerm);
      
      const res = await axios.get(`${API}/city-admin/${user.id}/customers-report?${params.toString()}`);
      setReport(res.data);
    } catch (err) {
      toast.error("Failed to load customers report");
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = (report?.customers || []).filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return c.customer_name?.toLowerCase().includes(search) || c.customer_phone?.includes(search);
  });

  const exportReport = () => {
    const csv = [
      ["Customer Name", "Phone", "Total Rides", "Completed Rides", "Total Spent", "Total Tips"].join(","),
      ...filteredCustomers.map(c => [
        c.customer_name, c.customer_phone, c.total_rides, c.completed_rides, c.total_spent, c.total_tips
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customers-report-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="city-admin-customers-report">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-500" /> Customers Report
          </h1>
          <p className="text-sm text-muted-foreground">Customers who booked rides in your city</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-blue-600">Total Customers</span>
          </div>
          <p className="text-3xl font-bold text-blue-700">{report?.summary?.total_customers || 0}</p>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-600">Total Rides</span>
          </div>
          <p className="text-3xl font-bold text-green-700">{report?.summary?.total_rides || 0}</p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <IndianRupee className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-purple-600">Total Spent</span>
          </div>
          <p className="text-3xl font-bold text-purple-700">₹{report?.summary?.total_spent?.toLocaleString() || 0}</p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <IndianRupee className="w-5 h-5 text-yellow-500" />
            <span className="text-sm text-yellow-600">Total Tips</span>
          </div>
          <p className="text-3xl font-bold text-yellow-700">₹{report?.summary?.total_tips?.toLocaleString() || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Name or phone..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                onKeyDown={(e) => e.key === "Enter" && fetchData()}
                className="pl-8" 
              />
            </div>
          </div>
          <div>
            <Label className="text-sm">Sort By</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="total_rides">Most Rides</SelectItem>
                <SelectItem value="total_spent">Highest Spent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={exportReport} className="w-full">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">Total Rides</TableHead>
              <TableHead className="text-right">Completed</TableHead>
              <TableHead className="text-right">Total Spent</TableHead>
              <TableHead className="text-right">Tips Given</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No customers found</TableCell>
              </TableRow>
            ) : (
              filteredCustomers.map((customer) => (
                <TableRow key={customer.customer_id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{customer.customer_name || "Unknown"}</p>
                      <p className="text-sm text-muted-foreground">{customer.customer_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{customer.total_rides}</TableCell>
                  <TableCell className="text-right text-green-600">{customer.completed_rides}</TableCell>
                  <TableCell className="text-right font-medium">₹{customer.total_spent?.toLocaleString()}</TableCell>
                  <TableCell className="text-right text-yellow-600">₹{customer.total_tips?.toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {customer.joined_at ? new Date(customer.joined_at).toLocaleDateString() : "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CityAdminCustomersReport;
